import React, { useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import { CoinIcon, FloatIcon } from './CurrencyIcons';
import IconImg from './IconImg';
import { iconUrl } from '../utils/assets';
import { useLocale } from '../i18n/LocaleContext';
import { TAP_FX_PROFILES } from '../utils/tapFx';
import { planetOrbit, planetFxOverlay } from '../utils/planetOrbit';
import { mergeClickerState, parseMoney, balanceWithPending } from '../utils/clickerMerge';
import {
  flushPendingTaps,
  getOutstandingTaps,
  isTapFlushing,
  discardOutstandingTaps,
  LOCAL_STORAGE_KEY_TAPS,
  LOCAL_STORAGE_KEY_ENERGY,
} from '../utils/tapFlush';

const TIMEOUT_DELAY = 1800;
const MAX_FLOATERS = 10;
/** Deepest burst is 16 particles; two bursts in flight is plenty to recycle. */
const MAX_SPARKS = 32;
/** How often a tap spend is allowed to reach App state (and re-render the app). */
const SHARED_ENERGY_INTERVAL_MS = 400;

function readStoredEnergy(fallback = NaN) {
  try {
    return parseMoney(localStorage.getItem(LOCAL_STORAGE_KEY_ENERGY), fallback);
  } catch (_) {
    return fallback;
  }
}

/**
 * Write to the DOM only when the visible string really changed.
 *
 * The RAF lerp below runs ~31 times a second whether or not the rounded digits
 * moved, and #score carries a 24px blurred glow — every assignment re-rasterises
 * that glow. Slow earners were paying for it dozens of times a second to draw
 * the exact same number.
 */
const setText = (el, text) => {
  if (el && el.textContent !== text) el.textContent = text;
};

const paintScore = (scoreEl, visual, value) => {
  visual.value = value;
  setText(scoreEl, Number(value).toFixed(2));
};

const paintEnergy = (textEl, barEl, visual, value, maxE) => {
  if (!Number.isFinite(value)) return;
  visual.value = value;
  const maxLabel = Number.isFinite(maxE) && maxE > 0 ? maxE : value;
  setText(textEl, `${Number(value).toFixed(2)} / ${Number(maxLabel).toFixed(2)}`);
  if (barEl) {
    const denom = Number.isFinite(maxE) && maxE > 0 ? maxE : 1;
    barEl.style.transform = `scaleX(${Math.min(1, Math.max(0, value / denom))})`;
  }
};

/** Raise the score target only — RAF lerp paints the hero digits (no snap). */
const bumpScoreTarget = (scoreRef, target) => {
  if (!Number.isFinite(target)) return;
  scoreRef.current = Math.max(scoreRef.current || 0, target);
};

const Main = ({ clickerData, onClickerUpdate, onSecurity, onRefresh }) => {
  const { t } = useLocale();
  // Prefer LS optimistic energy on remount. Do NOT subtract outstanding from
  // clickerData.energy — that field is already spent on each tap; double-debit
  // pinned the bar at 0 and triggered phantom recovery.
  //
  // Energy is a ref, never state: the digits and the bar are written straight to
  // the DOM by the RAF tick below, so a setState here bought nothing and cost a
  // full Main re-render on every tap and every regen second.
  const initialEnergyRef = useRef(null);
  if (initialEnergyRef.current === null) {
    const ls = readStoredEnergy(NaN);
    const fromProps = parseMoney(clickerData?.energy, NaN);
    initialEnergyRef.current = Number.isFinite(ls)
      ? Math.max(0, ls)
      : Number.isFinite(fromProps)
        ? Math.max(0, fromProps)
        : 0;
  }
  const initialEnergy = initialEnergyRef.current;

  const buttonRef = useRef(null);
  const scoreElementRef = useRef(null);
  const scoreCounterContainerRef = useRef(null);
  const energyTextRef = useRef(null);
  const energyBarInnerRef = useRef(null);

  const visualScoreRef = useRef({ value: 0, raf: 0 });
  const visualEnergyRef = useRef({ value: initialEnergy });
  const scoreRef = useRef(0);
  const energyRef = useRef(initialEnergy);
  // Never seed max with bare 5 — missing field must not clamp a real pool to 5.
  const maxEnergyRef = useRef(parseMoney(clickerData?.max_energy, NaN));
  const profitPerTapRef = useRef(0.01);
  const energyRegenRateRef = useRef(0.01);
  const prestigeMultRef = useRef(1);
  const coinPerHourRef = useRef(0);
  const floaterPoolRef = useRef([]);
  const sparkPoolRef = useRef([]);
  const timerRef = useRef(null);
  const sharedEnergyTimerRef = useRef(0);
  const fxTimersRef = useRef([]);
  const mountedRef = useRef(true);
  const tapFxRef = useRef(null);
  const passiveSyncRef = useRef(0);
  const lastTapPosRef = useRef({ x: 0.5, y: 0.5 });
  // Cache layout rects to avoid a forced reflow on every tap (mobile jank).
  const rectCacheRef = useRef({ t: 0, btn: null, stage: null });
  const energyHydratedRef = useRef(Number.isFinite(parseMoney(clickerData?.energy, NaN)));
  // While server holds offline income for claim, do NOT tick client-side passive
  const offlinePendingRef = useRef(false);
  const activePointerRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Tab remount: #energy-text starts empty. If logical == visual, RAF skips paint
  // and the counter stays blank until the next regen tick (~1s). Snap once.
  // Prefer React.useLayoutEffect so the CRA build always resolves the hook
  // (bare useLayoutEffect was once emitted without an import → blank screen).
  React.useLayoutEffect(() => {
    const en = energyRef.current;
    if (!Number.isFinite(en)) return;
    paintEnergy(
      energyTextRef.current,
      energyBarInnerRef.current,
      visualEnergyRef.current,
      en,
      maxEnergyRef.current
    );
  }, []);

  useEffect(() => {
    const invalidate = () => {
      rectCacheRef.current.t = 0;
    };
    window.addEventListener('resize', invalidate, { passive: true });
    window.addEventListener('orientationchange', invalidate, { passive: true });
    return () => {
      window.removeEventListener('resize', invalidate);
      window.removeEventListener('orientationchange', invalidate);
    };
  }, []);

  useEffect(() => {
    if (!clickerData) return;
    const balance = parseMoney(clickerData.balance);
    const ppt = parseMoney(clickerData.coin_per_click, 0.01);
    const serverEn = parseMoney(clickerData.energy, NaN);
    const rate = parseMoney(clickerData.energy_rate, NaN);
    const maxERaw = parseMoney(clickerData.max_energy, NaN);
    const pMult = parseMoney(clickerData.prestige_multiplier, 1) || 1;

    profitPerTapRef.current = ppt;
    if (Number.isFinite(rate)) energyRegenRateRef.current = rate;
    // Never fall back to bare "5" when the field is briefly missing — that capped
    // real energy pools and got persisted into sync_energy.
    if (Number.isFinite(maxERaw) && maxERaw > 0) maxEnergyRef.current = maxERaw;
    const maxE = maxEnergyRef.current;
    prestigeMultRef.current = pMult;
    tapFxRef.current = clickerData.equipped?.tap_fx || null;
    const cph = parseMoney(clickerData.coin_per_hour, NaN);
    if (Number.isFinite(cph)) coinPerHourRef.current = cph;
    offlinePendingRef.current = Boolean(
      clickerData?.offline?.earned || clickerData?.engagement?.offline?.earned
    );

    const outstanding = getOutstandingTaps();
    const flushing = isTapFlushing();

    // Do NOT discardOutstandingTaps() based on optimistic clickerData.energy.
    // Empty local energy + pending taps is normal mid-flush; discarding the
    // queue then trusting a stale high /sync is what created phantom energy.

    const outstandingNow = outstanding;

    // Authoritative balance target = server + still-queued AND in-flight taps.
    // Never paintScore here while tapping — each energy sync used to snap the
    // hero digits and kill the per-tap lerp animation.
    const targetBal = balanceWithPending(clickerData);
    if (outstandingNow > 0 || flushing) {
      bumpScoreTarget(scoreRef, targetBal);
      // Cold start only: seed visual once so RAF has something to lerp from.
      if (visualScoreRef.current.value <= 0 && scoreRef.current > 0) {
        paintScore(scoreElementRef.current, visualScoreRef.current, scoreRef.current);
      }
    } else {
      const cur = scoreRef.current;
      const maxPassiveLead = Math.max(0.2, (coinPerHourRef.current || 0) / 60);
      if (!Number.isFinite(cur) || cur <= 0 || cur < balance) {
        scoreRef.current = balance;
        paintScore(scoreElementRef.current, visualScoreRef.current, balance);
      } else if (cur > balance + maxPassiveLead) {
        // Wildly ahead of server with empty queue — discard stale inflation.
        scoreRef.current = balance;
        paintScore(scoreElementRef.current, visualScoreRef.current, balance);
      }
      // else keep local passive lead — RAF continues ticking toward scoreRef
    }

    // Energy:
    // - Queue empty → trust clickerData (server / regen).
    // - Queue active → LS is the live spend meter (already decremented per tap).
    //   NEVER do energy - outstanding*ppt here: clickerData.energy is already
    //   optimistic, so that double-debit slammed the bar to 0 in a few taps.
    if (Number.isFinite(serverEn)) {
      let nextEn;
      let persistLs = false;
      if (outstandingNow <= 0 && !flushing) {
        nextEn = serverEn;
        persistLs = true;
      } else {
        const lsEn = readStoredEnergy(NaN);
        if (Number.isFinite(lsEn)) {
          nextEn = Math.max(0, lsEn);
        } else if (Number.isFinite(energyRef.current)) {
          nextEn = Math.max(0, energyRef.current);
        } else {
          // Cold path only — no LS yet, assume serverEn is raw remote.
          nextEn = Math.max(0, serverEn - outstandingNow * ppt);
          persistLs = true;
        }
      }
      if (Number.isFinite(maxE) && maxE > 0) {
        nextEn = Math.min(maxE, Math.max(0, nextEn));
      } else {
        nextEn = Math.max(0, nextEn);
      }
      energyHydratedRef.current = true;
      energyRef.current = nextEn;
      // Snap on cold hydrate, empty DOM (tab remount), or large correction.
      const vis = visualEnergyRef.current.value;
      const snapGap = Math.max(1.5, (Number.isFinite(maxE) && maxE > 0 ? maxE : 5) * 0.35);
      const textEmpty = !energyTextRef.current?.textContent?.trim();
      if (!Number.isFinite(vis) || textEmpty || Math.abs(vis - nextEn) > snapGap) {
        paintEnergy(
          energyTextRef.current,
          energyBarInnerRef.current,
          visualEnergyRef.current,
          nextEn,
          maxE
        );
      }
      if (persistLs) {
        try {
          localStorage.setItem(LOCAL_STORAGE_KEY_ENERGY, nextEn.toFixed(2));
        } catch (_) {
          /* ignore */
        }
      }
    }
  }, [clickerData]);

  const haptic = () => {
    try {
      window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
    } catch (_) {
      /* ignore */
    }
  };

  // Publishing the spend into App state re-renders every screen under the
  // router, so a fast tapper used to trigger ~10 full reconciles per second.
  // localStorage is written on every tap and is what regen actually reads, so
  // React only needs to catch up a few times a second.
  const publishSharedEnergy = useCallback(() => {
    sharedEnergyTimerRef.current = 0;
    const value = energyRef.current;
    if (!Number.isFinite(value)) return;
    onClickerUpdate?.((prev) => {
      if (!prev) return prev;
      const prevEn = parseMoney(prev.energy, NaN);
      if (Number.isFinite(prevEn) && Math.abs(prevEn - value) < 1e-6) return prev;
      return { ...prev, energy: String(value) };
    });
  }, [onClickerUpdate]);

  const publishSharedEnergySoon = useCallback(() => {
    if (sharedEnergyTimerRef.current) return;
    sharedEnergyTimerRef.current = window.setTimeout(
      publishSharedEnergy,
      SHARED_ENERGY_INTERVAL_MS
    );
  }, [publishSharedEnergy]);

  // Button/stage positions are stable between layout changes, so cache the
  // rects for a short window instead of measuring on every tap.
  const getRects = () => {
    const cache = rectCacheRef.current;
    const now = performance.now();
    if (cache.btn && cache.stage && now - cache.t < 500) return cache;
    const btn = buttonRef.current;
    const stage = scoreCounterContainerRef.current;
    if (!btn) return cache;
    cache.btn = btn.getBoundingClientRect();
    cache.stage = stage ? stage.getBoundingClientRect() : cache.stage;
    cache.t = now;
    return cache;
  };

  const recordTapPos = (event) => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = getRects().btn || btn.getBoundingClientRect();
    if (!rect || rect.width <= 0 || rect.height <= 0) return;
    const touch = event.changedTouches?.[0] || event.touches?.[0];
    const cx = touch?.clientX ?? event.clientX;
    const cy = touch?.clientY ?? event.clientY;
    if (!Number.isFinite(cx) || !Number.isFinite(cy)) return;
    lastTapPosRef.current = {
      x: Math.min(1, Math.max(0, (cx - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (cy - rect.top) / rect.height)),
    };
  };

  const acquireFloater = () => {
    const pool = floaterPoolRef.current;
    let el = pool.pop();
    if (!el) {
      el = document.createElement('div');
      el.className = 'score-counter';
    }
    return el;
  };

  const releaseFloater = (el) => {
    // Detach first — mutating class/style on an attached node only buys a style
    // recalc for something about to leave the tree.
    if (el.parentNode) el.parentNode.removeChild(el);
    el.classList.remove('is-flying');
    el.removeAttribute('style');
    if (floaterPoolRef.current.length < MAX_FLOATERS) {
      floaterPoolRef.current.push(el);
    }
  };

  const trackTimeout = (fn, ms) => {
    const id = window.setTimeout(() => {
      fxTimersRef.current = fxTimersRef.current.filter((x) => x !== id);
      if (mountedRef.current) fn();
    }, ms);
    fxTimersRef.current.push(id);
    return id;
  };

  const spawnScoreCounter = (amount) => {
    const stage = scoreCounterContainerRef.current;
    const btn = buttonRef.current;
    if (!stage || !btn) return;
    const rects = getRects();
    const stageRect = rects.stage;
    const btnRect = rects.btn;
    if (!stageRect || !btnRect) return;
    const { x: rx, y: ry } = lastTapPosRef.current;
    const x = btnRect.left - stageRect.left + rx * btnRect.width - 20 + (Math.random() * 40 - 20);
    const y = btnRect.top - stageRect.top + ry * btnRect.height - 8 + (Math.random() * 16 - 8);
    const el = acquireFloater();
    el.textContent = `+${amount.toFixed(2)}`;
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
    // Класс ставим до вставки: элемент приходит из пула уже отсоединённым, его
    // анимация уничтожена вместе с прошлым прикреплением, поэтому она стартует
    // заново сама. Прежний `el.offsetWidth` дёргал синхронный layout всей
    // страницы на каждый тап — самый дорогой вызов во всём обработчике.
    el.classList.add('is-flying');
    stage.appendChild(el);
    trackTimeout(() => releaseFloater(el), 700);
  };

  /**
   * Burst of particles under the finger.
   *
   * Pooled and inserted as one fragment: a supernova skin fires 16 of these per
   * tap, and the old path created 16 elements, 16 separate insertions and 16
   * timers every time — on a slow phone that alone outweighed the tap itself.
   */
  const spawnTapSparks = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = getRects().btn || btn.getBoundingClientRect();
    if (!rect) return;
    const fx = tapFxRef.current;
    const hasFx = fx && fx !== 'none';
    const profile = (hasFx && TAP_FX_PROFILES[fx]) || TAP_FX_PROFILES.default;
    const className = hasFx ? `tap-spark fx-${fx}` : 'tap-spark fx-default';
    const { x: rx, y: ry } = lastTapPosRef.current;
    const px = rx * rect.width;
    const py = ry * rect.height;
    const count = profile.count;

    const frag = document.createDocumentFragment();
    const batch = [];
    for (let i = 0; i < count; i += 1) {
      const spark = sparkPoolRef.current.pop() || document.createElement('i');
      spark.className = className;
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.35;
      const dist = profile.dist + Math.random() * profile.spread;
      // Every particle spins from its own angle, in its own direction
      const rot0 = Math.random() * 360;
      const rot1 = rot0 + (90 + Math.random() * 220) * (Math.random() < 0.5 ? 1 : -1);
      const style = spark.style;
      style.left = `${px}px`;
      style.top = `${py}px`;
      style.setProperty('--sx', `${Math.cos(angle) * dist}px`);
      style.setProperty('--sy', `${Math.sin(angle) * dist - profile.rise}px`);
      style.setProperty('--rot', `${rot0.toFixed(1)}deg`);
      style.setProperty('--rot-end', `${rot1.toFixed(1)}deg`);
      style.animationDelay = `${i * 14}ms`;
      frag.appendChild(spark);
      batch.push(spark);
    }
    btn.appendChild(frag);
    trackTimeout(() => {
      const pool = sparkPoolRef.current;
      for (let i = 0; i < batch.length; i += 1) {
        const spark = batch[i];
        spark.remove();
        if (pool.length < MAX_SPARKS) {
          spark.removeAttribute('style');
          pool.push(spark);
        }
      }
    }, 720);
  };

  const applyTapResponse = useCallback(
    (responseData) => {
      if (!responseData) return;

      if (responseData.blocked) {
        onSecurity?.(responseData.security);
        onClickerUpdate?.((prev) =>
          mergeClickerState(prev, responseData, { preferRemoteBalance: true })
        );
        return;
      }

      // Tap payload is authoritative — never keep a higher optimistic React balance
      // on top of pending taps (that double-counted after tab remounts).
      onClickerUpdate?.((prev) =>
        mergeClickerState(prev, responseData, { preferRemoteBalance: true })
      );

      if (!mountedRef.current) return;

      const maxERaw = parseMoney(responseData.max_energy, NaN);
      if (Number.isFinite(maxERaw) && maxERaw > 0) maxEnergyRef.current = maxERaw;
      const maxE = maxEnergyRef.current;
      const en = parseMoney(responseData.energy, NaN);
      const ppt = parseMoney(responseData.coin_per_click, profitPerTapRef.current);
      const pMult = parseMoney(responseData.prestige_multiplier, prestigeMultRef.current) || 1;
      // Prefer shared formula over raw bal+outstanding.
      const targetBal = balanceWithPending({
        ...responseData,
        balance: responseData.balance,
        coin_per_click: ppt,
        prestige_multiplier: pMult,
      });
      const cph = parseMoney(responseData.coin_per_hour, NaN);
      if (Number.isFinite(cph) && cph > 0) coinPerHourRef.current = cph;

      profitPerTapRef.current = ppt;
      const rate = parseMoney(responseData.energy_rate, NaN);
      if (Number.isFinite(rate)) energyRegenRateRef.current = rate;
      prestigeMultRef.current = pMult;

      // Target only — keep the smooth score-ticking lerp while batches drain.
      bumpScoreTarget(scoreRef, targetBal);

      if (Number.isFinite(en)) {
        const remainingNow = getOutstandingTaps();
        let nextEn =
          remainingNow > 0 ? Math.max(0, en - remainingNow * ppt) : en;
        // Empty energy + leftover queue: drop queue so regen works
        if (remainingNow > 0 && en < ppt) {
          discardOutstandingTaps();
          nextEn = en;
        }
        if (Number.isFinite(maxE) && maxE > 0) nextEn = Math.min(maxE, nextEn);
        energyHydratedRef.current = true;
        energyRef.current = nextEn;
        // Target only — do not snap the energy digits (RAF lerps like score).
        const vis = visualEnergyRef.current.value;
        const snapGap = Math.max(1.5, (Number.isFinite(maxE) && maxE > 0 ? maxE : 5) * 0.35);
        if (!Number.isFinite(vis) || Math.abs(vis - nextEn) > snapGap) {
          paintEnergy(
            energyTextRef.current,
            energyBarInnerRef.current,
            visualEnergyRef.current,
            nextEn,
            maxE
          );
        }
        try {
          localStorage.setItem(LOCAL_STORAGE_KEY_ENERGY, nextEn.toFixed(2));
        } catch (_) {
          /* ignore */
        }
      }
    },
    [onClickerUpdate, onSecurity]
  );

  const sendCachedData = useCallback(async () => {
    try {
      // Apply every batch mid-drain so score/energy stay aligned with outstanding.
      // Also apply the final return (blocked/busy paths skip onBatch).
      const last = await flushPendingTaps({ onBatch: applyTapResponse });
      if (last?.blocked || last?.busy) applyTapResponse(last);
    } catch (error) {
      console.error('Error sending cached data:', error);
    }
  }, [applyTapResponse]);

  const pressVisual = (down) => {
    if (!buttonRef.current) return;
    buttonRef.current.classList.toggle('is-pressed', down);
  };

  const handleButtonPointerUp = (event) => {
    if (activePointerRef.current != null && event.pointerId !== activePointerRef.current) return;
    activePointerRef.current = null;
    recordTapPos(event);
    pressVisual(false);
    if (energyRef.current < profitPerTapRef.current) return;

    haptic();
    const earned = profitPerTapRef.current * prestigeMultRef.current;
    // Bump logical target; RAF tick lerps #score (do not paintScore here).
    scoreRef.current += earned;
    const newEnergy = Math.max(energyRef.current - profitPerTapRef.current, 0);
    energyRef.current = newEnergy;
    // Logical spend only — RAF lerps the energy counter (do not paint here).

    const pending = (parseInt(localStorage.getItem(LOCAL_STORAGE_KEY_TAPS), 10) || 0) + 1;
    localStorage.setItem(LOCAL_STORAGE_KEY_TAPS, String(pending));
    localStorage.setItem(LOCAL_STORAGE_KEY_ENERGY, newEnergy.toFixed(2));
    // Keep shared clickerData energy in lockstep so other tabs see the spend
    // and global regen continues from the correct value. Score is NOT written
    // here — writing balance would re-enter the sync effect and fight the lerp.
    publishSharedEnergySoon();

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      await sendCachedData();
      timerRef.current = null;
    }, TIMEOUT_DELAY);

    spawnScoreCounter(earned);
    spawnTapSparks();
  };

  useEffect(() => {
    /**
     * The hero balance and the energy meter are animated here, and nowhere else:
     * every frame the visual value takes a fixed fraction of its distance to the
     * logical one, which is what makes the digits glide instead of jump. That
     * glide is frame-based on purpose — closing the gap takes about thirty
     * frames, roughly a second, which is exactly the pace at which passive
     * income and energy regen refill it.
     *
     * So this loop runs every frame while anything is in motion. An earlier
     * version let it sleep between paints and compensated the lerp for the
     * elapsed time; that is correct arithmetic and completely wrong animation —
     * it closed each gap in a single step and the counters ticked over in
     * jumps. Do not reintroduce it.
     */
    let raf = 0;
    let last = performance.now();
    let paintAcc = 0;
    let glowUntil = 0;
    const scoreEl = () => scoreElementRef.current;

    const tick = (now) => {
      const dt = Math.min(0.05, Math.max(0, (now - last) / 1000));
      last = now;
      const hidden = document.visibilityState !== 'visible';
      if (hidden) {
        raf = 0;
        return;
      }
      const rate = (coinPerHourRef.current || 0) / 3600;
      // Server parks long offline for claim — client must not keep accruing on top
      if (rate > 0 && !offlinePendingRef.current) {
        scoreRef.current += rate * dt;
        passiveSyncRef.current += dt;
        if (passiveSyncRef.current >= 45 && !isTapFlushing()) {
          passiveSyncRef.current = 0;
          const syncPromise = onRefresh
            ? onRefresh({ preferRemoteBalance: true })
            : api.clickerSync();
          syncPromise
            .then((data) => {
              if (!data || isTapFlushing() || !mountedRef.current) return;
              const bal = parseMoney(data.balance, NaN);
              const cph = parseMoney(data.coin_per_hour, NaN);
              if (Number.isFinite(cph)) coinPerHourRef.current = cph;
              offlinePendingRef.current = Boolean(
                data?.offline?.earned || data?.engagement?.offline?.earned
              );
              if (Number.isFinite(bal) && getOutstandingTaps() <= 0) {
                // Reconcile upward only — never yank the hero score backward.
                if (bal > scoreRef.current) scoreRef.current = bal;
              }
              if (!onRefresh) {
                onClickerUpdate?.((prev) =>
                  mergeClickerState(prev, data, { preferRemoteBalance: true })
                );
              }
            })
            .catch(() => {});
        }
      }

      const eTarget = energyRef.current;
      const eVis = visualEnergyRef.current.value;
      const energyNeedsPaint =
        Number.isFinite(eTarget) &&
        (!Number.isFinite(eVis) || Math.abs(eVis - eTarget) > 0.0004);
      const needsPaint =
        rate > 0 ||
        Math.abs(visualScoreRef.current.value - scoreRef.current) > 0.0004 ||
        energyNeedsPaint;
      if (needsPaint) {
        paintAcc += dt;
        if (paintAcc >= 0.032) {
          paintAcc = 0;
          const target = scoreRef.current;
          const state = visualScoreRef.current;
          const cur = state.value;
          const delta = target - cur;
          const alpha = Math.abs(delta) > 1 ? 0.42 : Math.abs(delta) > 0.05 ? 0.22 : 0.12;
          const next = Math.abs(delta) < 0.0004 ? target : cur + delta * alpha;
          state.value = next;
          const el = scoreEl();
          if (el) {
            setText(el, next.toFixed(2));
            if (delta > 0.0002) {
              glowUntil = now + 420;
              el.classList.add('score-ticking');
            } else if (now > glowUntil) {
              el.classList.remove('score-ticking');
            }
          }

          // Energy counter / bar — same lerp as score; energyRef stays authoritative.
          // Slightly snappier on spend so each tap feels responsive.
          if (Number.isFinite(eTarget)) {
            const eState = visualEnergyRef.current;
            const eCur = Number.isFinite(eState.value) ? eState.value : eTarget;
            const eDelta = eTarget - eCur;
            const eAlpha =
              eDelta < 0
                ? Math.abs(eDelta) > 0.5
                  ? 0.55
                  : Math.abs(eDelta) > 0.05
                    ? 0.32
                    : 0.18
                : Math.abs(eDelta) > 1
                  ? 0.42
                  : Math.abs(eDelta) > 0.05
                    ? 0.22
                    : 0.12;
            const eNext = Math.abs(eDelta) < 0.0004 ? eTarget : eCur + eDelta * eAlpha;
            paintEnergy(
              energyTextRef.current,
              energyBarInnerRef.current,
              eState,
              eNext,
              maxEnergyRef.current
            );
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };

    const start = () => {
      if (raf) return;
      last = performance.now();
      raf = requestAnimationFrame(tick);
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') start();
      else if (raf) {
        cancelAnimationFrame(raf);
        raf = 0;
      }
    };
    start();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      if (raf) cancelAnimationFrame(raf);
      scoreEl()?.classList.remove('score-ticking');
    };
  }, [onClickerUpdate, onRefresh]);

  useEffect(() => {
    // Energy regen runs globally in AppLayout — do NOT tick it here (would double).
    // Flush pending taps on unmount only. Hide/pagehide is owned by AppLayout.
    const flushNow = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (getOutstandingTaps() > 0) {
        flushPendingTaps({ onBatch: applyTapResponse }).catch(() => {});
      }
    };

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      fxTimersRef.current.forEach((id) => clearTimeout(id));
      fxTimersRef.current = [];
      // Leaving the tab must not strand the last few taps' spend in the ref.
      if (sharedEnergyTimerRef.current) {
        clearTimeout(sharedEnergyTimerRef.current);
        publishSharedEnergy();
      }
      flushNow();
    };
  }, [applyTapResponse, publishSharedEnergy]);

  const planetAsset = clickerData?.equipped?.planet_asset || '';
  const planetCode = clickerData?.equipped?.planet || 'none';
  const tapFx = clickerData?.equipped?.tap_fx || 'none';
  const sphereSrc = planetAsset || 'button.png';
  const orbit = planetOrbit(planetCode);
  const fxOverlay = planetFxOverlay(planetCode);

  return (
    <main className="main screen-enter">
      <div id="score-container" className="score-hero">
        <CoinIcon size={40} className="score-coin" />
        <span ref={scoreElementRef} id="score" />
      </div>

      <div
        id="main-container"
        className={`tap-stage planet-${planetCode} fx-${tapFx}${orbit ? ' has-orbit' : ''}`}
      >
        <div className="tap-aura" aria-hidden />
        {orbit ? (
          <span
            className={`planet-orbit planet-orbit-back orbit-${orbit.kind}${orbit.spin ? ' is-spin' : ''}`}
            aria-hidden
          >
            <span className="planet-orbit-spin">
              <IconImg className="planet-deco" src={orbit.back} draggable={false} decoding="async" />
            </span>
          </span>
        ) : null}
        <button
          type="button"
          ref={buttonRef}
          id="button"
          className="tap-button"
          onPointerDown={(e) => {
            e.preventDefault();
            if (activePointerRef.current != null && e.pointerId !== activePointerRef.current) return;
            activePointerRef.current = e.pointerId;
            try {
              e.currentTarget.setPointerCapture?.(e.pointerId);
            } catch (_) {
              /* ignore */
            }
            recordTapPos(e);
            if (energyRef.current >= profitPerTapRef.current) pressVisual(true);
          }}
          onPointerMove={(e) => {
            if (activePointerRef.current != null && e.pointerId !== activePointerRef.current) return;
            if (e.buttons || e.pressure > 0) recordTapPos(e);
          }}
          onPointerUp={handleButtonPointerUp}
          onPointerCancel={(e) => {
            if (activePointerRef.current != null && e.pointerId !== activePointerRef.current) return;
            activePointerRef.current = null;
            pressVisual(false);
          }}
          onPointerLeave={(e) => {
            if (activePointerRef.current != null && e.pointerId !== activePointerRef.current) return;
            pressVisual(false);
          }}
          aria-label="Tap"
        >
          <IconImg
            className="tap-planet-img"
            src={sphereSrc}
            draggable={false}
            decoding="async"
            fetchPriority="high"
          />
          <span className={`planet-fx planet-fx-${planetCode}`} aria-hidden>
            {fxOverlay ? <IconImg src={fxOverlay} draggable={false} decoding="async" /> : null}
          </span>
        </button>
        {orbit ? (
          <span
            className={`planet-orbit planet-orbit-front orbit-${orbit.kind}${orbit.spin ? ' is-spin' : ''}`}
            aria-hidden
          >
            <span className="planet-orbit-spin">
              <IconImg className="planet-deco" src={orbit.front} draggable={false} decoding="async" />
            </span>
          </span>
        ) : null}
        <div ref={scoreCounterContainerRef} id="score-counter-container" />
      </div>

      <div id="energy-container" className="energy-panel glass-panel">
        <div className="energy-info-container">
          <div className="energy-count-container">
            <FloatIcon src={iconUrl('energy_new.png')} size={28} eager native animated />
            <span ref={energyTextRef} id="energy-text" />
          </div>
          <Link to="/clicker/upgrade" className="boost-button">
            <FloatIcon src={iconUrl('rocket.png')} size={22} eager native animated />
            <span className="nav-span-16">{t('boostCta')}</span>
          </Link>
        </div>
        <div id="energy-bar">
          <div ref={energyBarInnerRef} id="energy-bar-inner" />
        </div>
      </div>
    </main>
  );
};

export default Main;
